import * as React from 'react';
import {
  ACTION_PATH,
  DATA_REDIRECT_PATH,
  METHOD_PATH,
  REDIRECT_PATH,
} from '@packages/shared/render/plugins/Form/constants';
import {Expressions} from '@packages/systems/wfdl';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {trim} from 'lodash';
import {EEnum, EText} from '@packages/systems/wfdl-ast';
import {
  type ExpressionEditorDispatch,
  updateExpressionsAction,
} from '@packages/systems/ExpressionEditor-utils';
import {normalizeFormActionUrl} from '@packages/systems/designer/forms/utils';

const {getIn} = ContextualExpressions;

/**
 * Synchronization hook for form expression settings that derives values directly
 * from expressions and provides update functions for immediate dispatch.
 * Values automatically update when the expression prop changes.
 */
export const useFormExpressionSettings = (
  expr: ContextualExpression,
  expressionEditorDispatch: ExpressionEditorDispatch
) => {
  const actionValue = React.useMemo(() => {
    const action = getIn(expr, ACTION_PATH);
    if (!action) return '';
    const strippedAction = stripAll(action);
    return Expressions.getText(strippedAction) || '';
  }, [expr]);

  const methodValue = React.useMemo(() => {
    const method = getIn(expr, METHOD_PATH);
    // This value should always be set, but we'll default to post for now
    if (!method) return 'post';
    const strippedMethod = stripAll(method);
    return Expressions.getEnum(strippedMethod) || 'post';
  }, [expr]);

  const redirectValue = React.useMemo(() => {
    const redirect = getIn(expr, REDIRECT_PATH);
    if (!redirect) return '';
    const strippedRedirect = stripAll(redirect);
    return Expressions.getText(strippedRedirect) || '';
  }, [expr]);

  const updateActionAndMethod = React.useCallback(
    (newActionValue: string, newMethodValue: string) => {
      const newAction = normalizeFormActionUrl(newActionValue);
      const trimmedVal = trim(newAction);
      expressionEditorDispatch(
        updateExpressionsAction([
          {key: ACTION_PATH, newExpression: EText(trimmedVal)},
          {key: METHOD_PATH, newExpression: EEnum(newMethodValue)},
        ])
      );
    },
    [expressionEditorDispatch]
  );

  const updateRedirect = React.useCallback(
    (url: string) => {
      const trimmedExpr = EText(trim(url));
      expressionEditorDispatch(
        updateExpressionsAction([
          {key: REDIRECT_PATH, newExpression: trimmedExpr},
          {key: DATA_REDIRECT_PATH, newExpression: trimmedExpr},
        ])
      );
    },
    [expressionEditorDispatch]
  );

  return {
    actionValue,
    methodValue,
    redirectValue,
    updateActionAndMethod,
    updateRedirect,
  };
};
